import { CheckIcon, DatabaseIcon, DownloadIcon, FileCodeIcon, GitPullRequestIcon, LockIcon, MarkGithubIcon, MeterIcon, PencilIcon, PlusIcon, RepoIcon, RepoPushIcon, ShieldIcon, UnlockIcon } from "@primer/octicons-react";
import { rp } from "../api/client";
import { useState, type ReactNode } from "react";
import { Link, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useAction, useCi, useMe, usePolicy, useRemote, useRepo, useWhoami } from "../api/hooks";
import { canIn, type CiJobConfig, type ImportProvider, type Policy, type Remote, type RepoInfo, type RepoRow } from "../api/types";
import { CopyField, DescriptionList, Empty, ErrorMessage, Query, Sha, Stat, StatsRow, TimeAgo } from "../components/common";
import { DetailList } from "../components/Details";
import { bytes, cloneHost, duration } from "../components/format";
import { forge } from "../components/forge";
import { RepoConnection } from "../components/RepoConnection";
import { ReadOnlyContext, Subhead } from "../components/SettingsLayout";
import { Alert, AlertTitle } from "../components/ui/alert";
import { Badge, type Tone } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { CheckboxField } from "../components/ui/checkbox";
import { Field } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PRRemoteForm } from "./Remote";

/** Site admins, and admins of this repository through an access grant. */
export const useIsAdmin = (repo: string) => canIn(useWhoami().data, "admin", repo);

/** Repository settings: one sub-page per concern, picked from the Settings section of the repository menu. */
export function Settings() {
  const { repo = "" } = useParams();
  const admin = useIsAdmin(repo);
  return (
    <ReadOnlyContext.Provider value={!admin}>
      <Outlet />
    </ReadOnlyContext.Provider>
  );
}

/** Redirects a retired repo route (e.g. the old /settings/audit page) to its new home, keeping the query string. */
export function RepoRedirect({ to }: { to: string }) {
  const { repo = "" } = useParams();
  const { search } = useLocation();
  return <Navigate to={`${rp(repo)}${to}${search}`} replace />;
}

/** Sends a bare `/repo/...` URL (the old scheme) to `/owner/repo/...` once the owner is known. */
export function LegacyRepo() {
  const { repo = "", "*": rest = "" } = useParams();
  const { search } = useLocation();
  const q = useRepo(repo);
  if (q.isLoading) return null;
  const owner = q.data?.repo.owner;
  return <Navigate to={`${rp(repo, owner)}${rest ? `/${rest}` : ""}${search}`} replace />;
}

/** One repo admin call with its own success / error banner, so each sub-page reports only its own actions. */
function useRepoAction(repo: string) {
  const act = useAction(repo);
  const [result, setResult] = useState<{ label: string; data: unknown } | null>(null);
  const run = (label: string, path: string, body: unknown, method = "POST", onDone?: () => void) =>
    act.mutate(
      { method, path: `/v1/repos/${repo}${path}`, body },
      {
        onSuccess: (data) => {
          setResult({ label, data });
          onDone?.();
        },
      },
    );
  const feedback: ReactNode = (
    <>
      {result && !act.error && (
        <Alert variant="success">
          <AlertTitle>
            <CheckIcon /> {result.label}
          </AlertTitle>
          {result.data && typeof result.data === "object" ? <DetailList value={{ ...(result.data as Record<string, unknown>), status: undefined }} /> : null}
        </Alert>
      )}
      {act.error ? <ErrorMessage error={act.error} /> : null}
    </>
  );
  return { run, pending: act.isPending, feedback };
}

/* ---------- general ---------- */

export function SettingsGeneral() {
  const { repo = "" } = useParams();
  const r = useRepo(repo);
  const admin = useIsAdmin(repo);
  return (
    <>
      <Subhead title="General">What this repository is, where it came from, and where main stands.</Subhead>
      <Query q={r}>{({ repo: row, info }) => <General row={row} info={info} admin={admin} />}</Query>
    </>
  );
}

function General({ row, info, admin }: { row: RepoRow; info: RepoInfo; admin: boolean }) {
  const { run, pending, feedback } = useRepoAction(row.name);
  const [description, setDescription] = useState<string | null>(null);
  const value = description ?? row.description;
  return (
    <>
      <Card asChild className="mt-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            run("Description saved", "", { description: value.trim() }, "PATCH");
          }}
        >
          <CardHeader>
            <RepoIcon /> About
          </CardHeader>
          <CardContent className="grid gap-4">
            <Field label="Repository name" hint="Used in URLs, clone paths and agent tokens. It cannot be changed.">
              <CopyField value={row.name} title="Copy name" className="max-w-96 font-mono" />
            </Field>
            <Field label="Description" hint="Shown in the repository list and searched by the command palette.">
              <div className="flex items-center gap-2">
                <Input disabled={!admin} maxLength={500} value={value} onChange={(e) => setDescription(e.target.value)} placeholder="What agents work on here" />
                {admin && (
                  <Button variant="primary" disabled={pending || value.trim() === row.description}>
                    Save
                  </Button>
                )}
              </div>
            </Field>
          </CardContent>
        </form>
      </Card>

      <StatsRow>
        <Stat n={info.main ? <Sha repo={row.name} sha={info.main.commit_sha} n={7} /> : "—"} label={info.main ? `main · v${info.main.version}` : "main is not set yet"} />
        <Stat n={info.last ? <TimeAgo ms={info.last.ts} /> : "never"} label={info.last ? `last ${info.last.kind} by ${info.last.actor}` : "nothing has landed"} />
        <Stat n={<TimeAgo ms={row.created} />} label="created" />
      </StatsRow>

      <Card>
        <CardHeader>
          <DownloadIcon /> Origin
        </CardHeader>
        <CardContent>
          <Origin row={row} />
        </CardContent>
      </Card>
      {feedback}
    </>
  );
}

const IMPORT_STATE: Record<NonNullable<RepoRow["import_state"]>, { tone: Tone; text: string }> = {
  pending: { tone: "yellow", text: "Importing" },
  done: { tone: "green", text: "Imported" },
  failed: { tone: "red", text: "Import failed" },
};

function Origin({ row }: { row: RepoRow }) {
  if (!row.import_provider || !row.import_url) {
    return <p className="text-muted-foreground">Created empty in bee. Every commit on main was landed through a change.</p>;
  }
  const state = row.import_state ? IMPORT_STATE[row.import_state] : null;
  return (
    <>
      <DescriptionList start>
        <dt>Source</dt>
        <dd className="font-mono">{row.import_url}</dd>
        <dt>Branch</dt>
        <dd className="font-mono">{row.import_branch ?? "default branch"}</dd>
        {state && (
          <>
            <dt>Status</dt>
            <dd>
              <Badge tone={state.tone}>{state.text}</Badge>
            </dd>
          </>
        )}
      </DescriptionList>
      {row.import_error && <pre className="mt-3 whitespace-pre-wrap font-mono text-xs text-danger">{row.import_error}</pre>}
      <p className="mt-3 text-xs text-muted-foreground">
        A one-time copy with identical commit SHAs; the source is not kept in sync.
        {" To send landed work back to it, add it as the remote under Remote."}
      </p>
    </>
  );
}

/* ---------- policy ---------- */

export function SettingsPolicy() {
  const { repo = "" } = useParams();
  const policy = usePolicy(repo);
  const ci = useCi(repo);
  return (
    <>
      <Subhead
        title="Policy"
        actions={
          <Button asChild size="sm">
            <Link to={`${rp(repo)}/src/main/.bee/policy.yaml`}>
              <FileCodeIcon /> policy.yaml
            </Link>
          </Button>
        }
      >
        The rules every change must pass before it lands, read from <code>.bee/policy.yaml</code> on main. Agents cannot edit that file; change it through an admin change.
      </Subhead>
      <Query q={policy}>{(p) => <PolicyView repo={repo} policy={p} jobs={ci.data?.config?.jobs} />}</Query>
    </>
  );
}

const TIERS = ["low", "normal", "high"] as const;

const TIER_TONE: Record<(typeof TIERS)[number], Tone> = { low: "green", normal: "blue", high: "orange" };

/** Mirrors policy.Tier on the server: high wins on any path, low needs every path, normal is the rest. */
function tierScope(t: (typeof TIERS)[number], paths: string[]): ReactNode {
  if (t === "normal") return <span className="text-muted-foreground">Everything that is neither low nor high risk</span>;
  if (!paths.length) return <span className="text-muted-foreground">No paths, so no change is ever {t} risk</span>;
  return (
    <>
      <span className="text-muted-foreground">{t === "high" ? "Any path matches " : "Every path matches "}</span>
      <Globs globs={paths} />
    </>
  );
}

function Globs({ globs, tone }: { globs: string[]; tone?: Tone }) {
  return (
    <span className="inline-flex flex-wrap gap-1 align-middle">
      {globs.map((g) => (
        <Badge key={g} tone={tone} className="font-mono">
          {g}
        </Badge>
      ))}
    </span>
  );
}

/** What gates a tier, resolved against ci.yaml: `jobs: all` lists the real jobs, and names ci.yaml lacks are flagged. */
function TierGate({ tier, jobs }: { tier: Policy["tiers"]["low"]; jobs?: CiJobConfig[] }) {
  const defined = jobs?.map((j) => j.name);
  const pool = tier.jobs ?? defined ?? [];
  const extra = tier.require.filter((r) => !pool.includes(r));
  if (!pool.length && !extra.length) {
    return <Badge tone="yellow">{tier.jobs === null && jobs !== undefined ? "No ci.yaml jobs: lands without CI" : "Lands without CI"}</Badge>;
  }
  const missing = defined ? [...pool, ...extra].filter((n) => !defined.includes(n)) : [];
  const filtered = new Set(jobs?.filter((j) => j.paths?.length).map((j) => j.name));
  return (
    <div className="grid gap-1.5">
      <span className="inline-flex flex-wrap gap-1">
        {[...pool, ...extra].map((n) => {
          const required = tier.require.includes(n);
          const pathOnly = filtered.has(n) && !required;
          return (
            <Badge
              key={n}
              tone={missing.includes(n) ? "red" : required ? "purple" : "default"}
              className="font-mono"
              title={required ? "Required: runs whatever the change touches" : pathOnly ? "Runs only when the change touches this job's paths" : undefined}
            >
              {n}
              {pathOnly ? "*" : ""}
            </Badge>
          );
        })}
      </span>
      {tier.jobs === null && <span className="text-xs text-muted-foreground">All ci.yaml jobs</span>}
      {missing.length > 0 && <span className="text-xs text-danger">Not in ci.yaml: {missing.join(", ")}. Changes in this tier cannot start CI.</span>}
    </div>
  );
}

function PolicyView({ repo, policy, jobs }: { repo: string; policy: Policy; jobs?: CiJobConfig[] }) {
  const hasFiltered = jobs?.some((j) => j.paths?.length);
  const hasRequired = TIERS.some((t) => policy.tiers[t]?.require.length);
  return (
    <>
      {policy.freeze && (
        <Alert variant="warning" className="mt-0">
          <AlertTitle>
            <LockIcon /> Landing is frozen by policy
          </AlertTitle>
          <code>freeze: true</code> in policy.yaml stops every change from landing. The Danger zone cannot lift it; remove the line in an admin change.
        </Alert>
      )}

      <Card className="mt-0">
        <CardHeader>
          <ShieldIcon /> Risk tiers
        </CardHeader>
        <CardContent className="border-b text-muted-foreground">
          Each change gets one tier from the paths it touches, and the tier decides which CI jobs must pass before it lands.
        </CardContent>
        <Table>
          <TableHeader>
            <tr>
              <TableHead>Tier</TableHead>
              <TableHead>A change is in this tier when</TableHead>
              <TableHead>Must pass</TableHead>
              <TableHead className="max-md:hidden">After rebase</TableHead>
            </tr>
          </TableHeader>
          <TableBody>
            {TIERS.map((t) => {
              const tier = policy.tiers[t];
              if (!tier) return null;
              return (
                <TableRow key={t}>
                  <TableCell>
                    <Badge tone={TIER_TONE[t]}>{t}</Badge>
                  </TableCell>
                  <TableCell className="text-xs">{tierScope(t, tier.paths)}</TableCell>
                  <TableCell className="text-xs">
                    <TierGate tier={tier} jobs={jobs} />
                  </TableCell>
                  <TableCell className="text-xs max-md:hidden">{tier.rerun_after_rebase ? "CI runs again on the exact commit that lands" : <span className="text-muted-foreground">Keeps the evidence from before the rebase</span>}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <CardContent className="border-t text-xs text-muted-foreground">
          {hasFiltered && <>* runs only when the change touches the job's <code>paths</code> in ci.yaml. </>}
          {hasRequired && <>Purple jobs are required and run whatever the change touches. </>}
          Jobs a selected job <code>needs</code> run first. <Link to={`${rp(repo)}/ci`}>See the job graph</Link>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <LockIcon /> Protected paths
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-muted-foreground">
            Agents cannot create or land a change that touches these; only admins can. <code>.bee/**</code> is always protected so agents cannot weaken their own gate.
          </p>
          <Globs globs={policy.protected} tone="yellow" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <MeterIcon /> Limits
        </CardHeader>
        <CardContent>
          {Object.entries(policy.limits).map(([k, v]) => {
            const meta = LIMITS[k];
            return (
              <SettingRow key={k} title={meta?.title ?? k.replace(/_/g, " ")} desc={meta?.desc ?? ""}>
                <span className="font-mono text-base font-semibold tabular-nums">
                  {v}
                  {meta?.unit && <span className="ml-1 text-xs font-normal text-muted-foreground">{meta.unit}</span>}
                </span>
              </SettingRow>
            );
          })}
        </CardContent>
      </Card>
    </>
  );
}

/** What each policy.yaml limit enforces (see changes.Pipeline). */
const LIMITS: Record<string, { title: string; desc: string; unit: string }> = {
  changes_per_agent_per_hour: { title: "Changes per agent per hour", desc: "An agent that opens more in a rolling hour is refused until older ones age out. Admins are exempt.", unit: "changes" },
  ci_minutes_per_task: { title: "CI budget per task", desc: "Once a task's changes have used this much CI time, its next land is refused until a human raises the budget.", unit: "min" },
  max_files_per_change: { title: "Files per change", desc: "Bigger changes are refused; split the work.", unit: "files" },
  max_base_behind: { title: "Base staleness", desc: "A change based further behind main than this is refused at land; the agent refreshes and lands again.", unit: "commits" },
};

/* ---------- remote ---------- */

type RemoteKind = ImportProvider | "git";

const KIND_NAME: Record<RemoteKind, string> = { github: "GitHub", git: "git host" };

function KindIcon({ kind, size }: { kind: RemoteKind; size?: number }) {
  if (kind === "github") return <MarkGithubIcon size={size} />;
  return <RepoPushIcon size={size} />;
}

/** The provider behind a git URL: a sign-in provider's host first, then the host name. */
function useRemoteKind(url: string): { kind: RemoteKind; host: string } | null {
  const providers = useMe().data?.providers;
  const host = cloneHost(url.trim());
  if (!host) return null;
  const p = providers?.find((p) => p.host === host);
  if (p) return { kind: p.id, host };
  if (host.includes("github")) return { kind: "github", host };
  return { kind: "git", host };
}

/** The repo's one remote, as bee admin remote shows it: batch PRs to GitHub, with human commits pulled back. */
export function SettingsRemote() {
  const { repo = "" } = useParams();
  const r = useRepo(repo);
  const remote = useRemote(repo);
  const admin = useIsAdmin(repo);
  return (
    <>
      <Subhead
        title="Remote"
        actions={
          remote.data?.remote && (
            <Button asChild size="sm">
              <Link to={`${rp(repo)}/remote`}>
                <GitPullRequestIcon /> Batches
              </Link>
            </Button>
          )
        }
      >
        The GitHub repository that gets what agents land. Agents always work against bee; the remote is for people, reviews and pipelines on
        the other side.
      </Subhead>
      <RepoConnection repo={repo} admin={admin} />
      <Query q={remote}>
        {(st) => (
          <Query q={r}>
            {({ repo: row }) =>
              st.remote ? (
                <PRRemote key={st.remote.url} repo={repo} rem={st.remote} admin={admin} />
              ) : (
                <NoRemote row={row} admin={admin} />
              )
            }
          </Query>
        )}
      </Query>
    </>
  );
}

/** A remote: the summary, with the settings form only opened to edit it. */
function PRRemote({ repo, rem, admin }: { repo: string; rem: Remote; admin: boolean }) {
  const [editing, setEditing] = useState(false);
  if (editing && admin) return <PRRemoteForm repo={repo} rem={rem} admin={admin} onClose={() => setEditing(false)} />;
  return (
    <RemoteSummary
      kind={rem.provider}
      url={rem.url}
      actions={
        admin && (
          <Button size="sm" onClick={() => setEditing(true)}>
            <PencilIcon /> Edit
          </Button>
        )
      }
    >
      <p>
        What agents land ships as batch {forge(rem.provider).review}s, one at a time, and commits humans push to <code>{rem.branch}</code> are pulled into main.
      </p>
      <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1.5 text-foreground">
        <dt className="text-muted-foreground">Branch</dt>
        <dd className="m-0 font-mono">{rem.branch}</dd>
        <dt className="text-muted-foreground">Batch size</dt>
        <dd className="m-0">{rem.batch_size} lands</dd>
        <dt className="text-muted-foreground">Max wait</dt>
        <dd className="m-0">{duration(rem.batch_wait_ms) || "0s"}</dd>
        <dt className="text-muted-foreground">Auto-merge</dt>
        <dd className="m-0">{rem.auto_merge ? `On (${rem.merge_method})` : "Off, a human merges"}</dd>
      </dl>
    </RemoteSummary>
  );
}

function RemoteSummary({ kind, url, actions, children }: { kind: RemoteKind; url: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <Card className="mt-0">
      <CardHeader>
        <KindIcon kind={kind} /> <span className="truncate font-mono">{url}</span>
        <span className="ml-auto" />
        {actions}
      </CardHeader>
      <CardContent className="text-muted-foreground">{children}</CardContent>
    </Card>
  );
}

function NoRemote({ row, admin }: { row: RepoRow; admin: boolean }) {
  const [adding, setAdding] = useState(false);
  if (adding && admin) return <AddRemoteForm repo={row.name} initial={row.import_url ?? ""} onCancel={() => setAdding(false)} />;
  const source = row.import_provider ? KIND_NAME[row.import_provider] : null;
  return (
    <Card className="mt-0">
      <Empty icon={RepoPushIcon}>
        <b>No remote</b>
        <div className="text-xs">
          {source ? `Imported from ${source}, but landed work is not sent back. ` : "main lives only on bee. "}
          Add a GitHub repository as the remote to send it there.
        </div>
        {admin && (
          <Button variant="primary" className="mt-3" onClick={() => setAdding(true)}>
            <PlusIcon /> {source ? `Add ${source} as remote` : "Add remote"}
          </Button>
        )}
      </Empty>
    </Card>
  );
}

/** bee admin remote add: the URL picks the provider (GitHub). */
function AddRemoteForm({ repo, initial, onCancel }: { repo: string; initial: string; onCancel: () => void }) {
  const add = useAction(repo);
  const connections = useMe().data?.connections;
  const [url, setUrl] = useState(initial);
  const [autoMerge, setAutoMerge] = useState(false);
  const target = useRemoteKind(url);
  const provider = target && target.kind !== "git" ? target.kind : null;
  const f = forge(provider);
  const https = url.trim().startsWith("https://");
  const account = https && target ? connections?.find((c) => c.host === target.host && c.status === "ok") : undefined;
  const pending = add.isPending;
  return (
    <Card asChild className="mt-0">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (provider) add.mutate({ method: "PUT", path: `/v1/repos/${repo}/remote`, body: { url: url.trim(), provider, branch: "main", auto_merge: autoMerge } });
        }}
      >
        <CardHeader>
          {target ? <KindIcon kind={target.kind} /> : <RepoPushIcon />} Add remote
        </CardHeader>
        <CardContent className="grid gap-4">
          <Field
            label="Repository URL"
            hint={
              !target ? (
                "A GitHub repository, over HTTPS or SSH."
              ) : account ? (
                <>
                  bee uses your {KIND_NAME[account.provider]} account <b>{account.login}</b>, which needs write access.
                </>
              ) : https ? (
                <>
                  No {KIND_NAME[target.kind]} account of yours is connected for {target.host}. <Link to="/profile?tab=account">Connect one</Link> with write access.
                </>
              ) : (
                "Over SSH bee pushes with its own key, which needs write access."
              )
            }
          >
            <Input mono autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://github.com/acme/shop.git" required />
          </Field>
          <CheckboxField
            checked={autoMerge}
            onCheckedChange={(v) => setAutoMerge(v === true)}
            title={`Merge batch ${f.short}s automatically`}
            description={`Once ${f.name} reports them ${f.clean}. Branch, batch size and timing can be changed after adding.`}
          />
        </CardContent>
        <CardContent className="flex flex-wrap items-center gap-2 border-t">
          <Button variant="primary" disabled={pending || !provider}>
            Add remote
          </Button>
          <Button type="button" disabled={pending} onClick={onCancel}>
            Cancel
          </Button>
          <code className="ml-auto truncate text-xs text-muted-foreground">
            bee admin remote add {url.trim() || "<url>"}
            {autoMerge ? " --auto-merge" : ""}
          </code>
        </CardContent>
        {add.error ? (
          <CardContent>
            <ErrorMessage error={add.error} />
          </CardContent>
        ) : null}
      </form>
    </Card>
  );
}

/* ---------- storage ---------- */

export function SettingsStorage() {
  const { repo = "" } = useParams();
  const r = useRepo(repo);
  const admin = useIsAdmin(repo);
  const { run, pending, feedback } = useRepoAction(repo);
  return (
    <>
      <Subhead title="Storage">Objects and bundles behind this repository, and the maintenance jobs that keep them lean and recoverable.</Subhead>
      <Query q={r}>
        {({ info }) => (
          <StatsRow className="mt-0">
            <Stat n={info.objects} label="objects" />
            <Stat n={info.bundles.n} label={`live bundles · ${bytes(info.bundles.bytes)}`} />
            <Stat n={`v${info.main?.version ?? 0}`} label="main version" />
          </StatsRow>
        )}
      </Query>
      {admin && (
        <Card>
          <CardHeader>
            <DatabaseIcon /> Maintenance
          </CardHeader>
          <CardContent>
            <SettingRow title="Compact" desc="Rewrite live bundles into large base bundles. Every object is kept.">
              <Button size="sm" disabled={pending} onClick={() => run("Compacted", "/admin/compact", { force: true, gc: false })}>
                Run
              </Button>
            </SettingRow>
            <SettingRow title="Compact + GC" desc="Compact, and drop objects no logged ref or change from the last 30 days can reach.">
              <Button size="sm" disabled={pending} onClick={() => run("Compacted and garbage-collected", "/admin/compact", { force: true, gc: true })}>
                Run
              </Button>
            </SettingRow>
            <SettingRow title="Rebuild check" desc="Rebuild refs and the object index from object storage alone and compare them with live state.">
              <Button size="sm" disabled={pending} onClick={() => run("Rebuild check finished", "/admin/rebuild-check", {})}>
                Run
              </Button>
            </SettingRow>
            <SettingRow title="Flush log to R2" desc="Mirror unwritten ref-log rows to object storage now instead of on the next batch.">
              <Button size="sm" disabled={pending} onClick={() => run("Log flushed", "/admin/log-flush", {})}>
                Run
              </Button>
            </SettingRow>
          </CardContent>
        </Card>
      )}
      {feedback}
    </>
  );
}

/** A titled setting with its control on the right; consecutive rows are divided by a rule. */
function SettingRow({ title, desc, children }: { title: string; desc: string; children?: ReactNode }) {
  return (
    <div data-slot="setting-row" className="flex flex-wrap items-center gap-4 [[data-slot=setting-row]+&]:mt-4 [[data-slot=setting-row]+&]:border-t [[data-slot=setting-row]+&]:border-border-muted [[data-slot=setting-row]+&]:pt-4">
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{title}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
      {children}
    </div>
  );
}

/* ---------- danger zone ---------- */

export function SettingsDanger() {
  const { repo = "" } = useParams();
  const r = useRepo(repo);
  const admin = useIsAdmin(repo);
  const { run, pending, feedback } = useRepoAction(repo);
  return (
    <>
      <Subhead title="Danger zone">Stop changes from landing on main.</Subhead>
      <Query q={r}>
        {({ info }) => (
          <Card className="mt-0 border-danger-emphasis">
            <CardHeader className="border-danger-muted bg-transparent">
              <LockIcon /> Landing
            </CardHeader>
            <CardContent>
              <SettingRow
                title={info.frozen ? "Landing is frozen" : "Freeze landing"}
                desc={info.frozen ? "No change can land on main until it is unfrozen." : "Stop every agent from landing on main. Changes keep running CI and queue up."}
              >
                {admin && (
                  <Button variant={info.frozen ? "primary" : "danger"} disabled={pending} onClick={() => run(info.frozen ? "Unfrozen" : "Frozen", "/admin/freeze", { freeze: !info.frozen })}>
                    {info.frozen ? <UnlockIcon /> : <LockIcon />} {info.frozen ? "Unfreeze" : "Freeze all lands"}
                  </Button>
                )}
              </SettingRow>
              {info.frozen_paths.length > 0 && (
                <Alert variant="warning">
                  <AlertTitle>Frozen paths (after an inconclusive verification)</AlertTitle>
                  <div className="flex flex-wrap gap-1.5">
                    {info.frozen_paths.map((p) => (
                      <Badge key={p} tone="yellow" className="font-mono">
                        {p}
                      </Badge>
                    ))}
                  </div>
                  {admin && (
                    <Button size="sm" className="mt-2.5" disabled={pending} onClick={() => run("Frozen paths cleared", "/admin/freeze", { freeze: info.frozen, paths: [] })}>
                      Clear frozen paths
                    </Button>
                  )}
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
      </Query>
      {feedback}
    </>
  );
}
